// FeuilleFormulaire.jsx — « MONTRE-MOI L'ENSEMBLE AVANT DE VALIDER ».
//
// ══ CE COMPOSANT NE CALCULE RIEN ═══════════════════════════════════════════
//
// Le serveur compose une STRUCTURE (`lib/formulaire-feuille.js`) ; ce fichier
// la rend, et c'est tout. Aucune logique n'est dupliquée — donc aucun miroir
// ADR-021 à tenir, et aucune divergence possible entre ce qui est décidé et ce
// qui est affiché.
//
// ⚠️ ET AUCUNE VALEUR NE VIENT DU TEXTE DU MODÈLE. Elle vient de ce que Dimitri
// a dit (validé par le registre) ou d'une lecture relue à l'instant. C'est
// vérifié côté serveur par un test qui injecte un faux montant dans la prose
// et vérifie qu'il n'atteint jamais la feuille.
//
// ══ TROIS RÈGLES DE RENDU, NON NÉGOCIABLES ═════════════════════════════════
//
//   1. CHAQUE VALEUR PORTE SA PROVENANCE, et il n'y en a que trois :
//      « tu m'as dit » · « lu dans X » · « manquant ». « Deviné » n'existe pas
//      — pas parce qu'on l'a interdit à l'affichage, mais parce que la
//      structure de l'état ne sait pas le représenter.
//   2. UN MANQUANT S'AFFICHE, il ne se remplit pas d'un exemple. Une case
//      grisée qui montre « 06 12 34 56 78 » finit recopiée un jour où
//      quelqu'un est pressé.
//   3. UNE ALERTE L'EMPORTE SUR TOUT : quand il y en a une, la carte de geste
//      n'est pas rendue (le serveur ne la compose même pas) et il ne reste que
//      la porte vers l'écran. On ne valide pas ce qu'on ne comprend pas.

import { DS } from "../../lib/ui";

const BORD = DS.border;

function Provenance({ provenance }) {
  if (provenance === "dit")
    return <span style={{ color: "#5c6773" }}>tu me l'as dit</span>;
  if (provenance && provenance.lu)
    return <span style={{ color: "#5c6773" }}>lu dans {provenance.lu}</span>;
  return (
    <span style={{ color: "#a16207" }} data-testid="feuille-manquant">
      manquant
    </span>
  );
}

export default function FeuilleFormulaire({ feuille }) {
  if (!feuille || !Array.isArray(feuille.lignes)) return null;
  const alertes = feuille.alertes || [];
  const manquants = feuille.manquants || [];

  return (
    <div
      data-testid="operateur-feuille-formulaire"
      data-pret={feuille.pret ? "oui" : "non"}
      style={{
        marginTop: 10,
        border: `1px solid ${BORD}`,
        borderRadius: DS.radiusCard,
        background: "#fff",
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "10px 12px 11px" }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: DS.textPrimary }}>
          {feuille.titre}
        </div>

        <dl
          data-testid="feuille-lignes"
          style={{
            margin: "8px 0 0",
            display: "grid",
            gridTemplateColumns: "auto 1fr auto",
            gap: "3px 10px",
            fontSize: 11.5,
            alignItems: "baseline",
          }}
        >
          {feuille.lignes.map((l) => (
            <div key={l.cle} style={{ display: "contents" }}>
              <dt style={{ color: "#5c6773" }}>
                {l.libelle}
                {l.facultatif ? " (facultatif)" : ""}
              </dt>
              <dd
                style={{
                  margin: 0,
                  fontWeight: 600,
                  color: l.valeur === null ? "#9aa5b1" : DS.textPrimary,
                }}
              >
                {/* ⚠️ Un tiret, jamais un exemple. */}
                {l.valeur === null ? "—" : l.valeur}
              </dd>
              <dd style={{ margin: 0, fontSize: 10.5, textAlign: "right" }}>
                <Provenance provenance={l.provenance} />
              </dd>
            </div>
          ))}
        </dl>

        {alertes.length > 0 && (
          <div
            data-testid="feuille-alertes"
            style={{
              margin: "10px 0 0",
              padding: "8px 10px",
              borderRadius: DS.radiusControl,
              background: "#fef3c7",
              border: "1px solid #fcd34d",
              fontSize: 11.5,
              color: "#78350f",
            }}
          >
            {alertes.map((a) => (
              <div key={a.code}>⚠ {a.dit}</div>
            ))}
            <div style={{ marginTop: 6, fontWeight: 600 }}>
              Je ne prépare rien tant que ce n'est pas tranché.
            </div>
          </div>
        )}

        {manquants.length > 0 && (
          <p
            data-testid="feuille-manquants"
            style={{ margin: "8px 0 0", fontSize: 11.5, color: "#a16207" }}
          >
            Il me manque encore : {manquants.map((m) => m.libelle).join(", ")}.
          </p>
        )}

        {(feuille.notes || []).map((n) => (
          <p
            key={n}
            style={{ margin: "6px 0 0", fontSize: 11, color: "#5c6773" }}
          >
            {n}
          </p>
        ))}

        <p style={{ margin: "8px 0 0", fontSize: 11, color: "#5c6773" }}>
          À constater dans {feuille.ou}.
        </p>
      </div>
    </div>
  );
}
